// @ts-nocheck
import React, { useState, useEffect, useMemo, useReducer, useCallback } from 'react';
import { DisplaySettings, FastForward } from '@mui/icons-material';
import { Box, TextField, Switch, FormControlLabel, Alert } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import AtomButton from '@/components/atoms/Button';
import AtomDropdown from '@/components/atoms/DropDown';
import AtomTreeView, { TreeViewNode } from '@/components/atoms/TreeView';
import OrgActionMenu from '@/components/molecules/ActionMenu';
import OrgDataTable from '@/components/organisms/DataTable';
import OrgDialog from '@/components/organisms/Dialog';
import { useSafeEffect } from '@/hooks/useSafeEffect';
import { AppDispatch } from '@/store';
import { selectCurrentYear } from '@/store/study-year';
import { studyYearCategories } from '@/store/study-year-categories';
import styles from './forecast.module.css';
import ForecastLoadingState from './ForecastLoadingState';
import RevenueForecastChart from './RevenueForecastChart';
import { getStudyProfile, getStudyName, getYearSelected } from '../../store/studies/selectors';
import {
  // selectSegmentMarketData,
  selectStudyYearsLoading,
  selectStudyYearsError,
  selectStudyYearSegments,
} from '../../store/study-year/selectors';
import { studyYears } from '../../store/study-year/thunks';
import {
  // selectChildCategories,
  selectCategoryChildData,
  selectCategoryForecasts,
} from '../../store/study-year-categories/selectors';

// Import the new generic forecast utilities

import {
  Category,
  TotalItem,
  TotalRow,
  forecastReducer,
  ForecastRow,
  forecastState,
  ForecastUtils,
} from '../../utils/newForecast';

interface AnalysisSubmitData {
  id: string;
  analysis: string;
}

interface ForecastDialogProps {
  row: ForecastRow;
  open: boolean;
  onClose: () => void;
  submitData: (data: AnalysisSubmitData) => void;
}

const ForecastAnalysisDialog: React.FC<ForecastDialogProps> = ({ // MOVE TO ACTIONS FOLDER
  open,
  onClose,
  submitData,
  row,
}) => {
  if (!row) {
    return null;
  }
  console.log('Rendering ForecastAnalysisDialog for row:', row);
  const [analysisText, setAnalysisText] = useState<string>(row?.analysis || '');

  //-------------------- EFFECTS --------------------
  useEffect(() => {
    setAnalysisText(row?.analysis || '');
  }, [row]);

  return (
    <OrgDialog
      open={open}
      maxWidth='md'
      onClose={onClose}
      title="Edit Forecast Analysis"
      dialogActions={[
        { label: 'Save', onClick: () => submitData({ id: row?.id, analysis: analysisText }) },
        { label: 'Cancel', onClick: onClose },
      ]}
    >
      <TextField
        value={analysisText}
        onChange={e => setAnalysisText(e.target.value)}
        multiline
        
        rows={4}
        fullWidth
        variant="outlined"
        label="Analysis"
        sx={{ marginTop: 2, marginBottom: 2 }}
      />
    </OrgDialog>
  );
};

const Forecast: React.FC = () => {
 //console.log('rendering forecast component');
  // #region ==================== DISPATCH ====================
  const dispatch = useDispatch<AppDispatch>();
  // #endregion

  // #region ================== SELECTORS ================
  const useChildCategoryData = useSelector(selectCategoryChildData);
  const isLoading = useSelector(selectStudyYearsLoading);
  const error = useSelector(selectStudyYearsError);
  const useStudyProfile = useSelector(getStudyProfile);
  const useSegments = useSelector(selectStudyYearSegments);
  const useStudyName = useSelector(getStudyName);
  const useYearSelected = useSelector(getYearSelected);
  const useCategoryForecasts = useSelector(selectCategoryForecasts);
  const useCurrentYear = useSelector(selectCurrentYear);

  // #endregion

  // #region ==================== STATES ====================
  const [growthSortBy, setGrowthSortBy] = useState('category');
  const [revenueSortBy, setRevenueSortBy] = useState('category');
  // Add these to your existing states section
  const [growthSortDirection, setGrowthSortDirection] = useState<'asc' | 'desc'>('asc');
  const [revenueSortDirection, setRevenueSortDirection] = useState<'asc' | 'desc'>('asc');
  const [autoUpdate, setAutoUpdate] = useState(false);
  const [balanceWarning, setBalanceWarning] = useState(false);
  const [originalRevenueData, setOriginalRevenueData] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState<string>('World Region');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedCategoryRoot, setSelectedCategoryRoot] = useState<Category | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');

  const [selectedRow, setSelectedRow] = useState<ForecastRow | null>(null);
  const [editAnalysisOpen, setEditAnalysisOpen] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');

  const [field, setField] = useState<'revenue' | 'units '>('revenue'); // Field to display in the data table
  const [viewYoY, setViewYoY] = useState(false); // Toggle for YoY view
  const [isAnchor, setIsAnchor] = useState(false); // State to track if the selected category is an anchor

  const [forecastState, forecastDispatch] = useReducer(forecastReducer, {
    forecastRows: {},
    adjustmentRows: {},
    totals: {},
    columnAgg: {},
    isAnchor: false,
    lastModified: new Date(),
    totalRow: { id: 'total', categoryName: 'Total', cagrRevenue: 0, cagrUnits: 0 },
  });
  // #endregion

  // #region ================== FUNCTIONS ================

  const handleCellValueChange = (yearOffset: number, categoryId: string, factor: number) => {
    //console.log('handling value change', yearOffset, categoryId, factor);
    forecastDispatch({
      type: 'UPDATE_FORECAST',
      payload: { adjustment: { yearOffset, categoryId, adjustmentValue: factor } },
    });
  };

  const handleAnalysisOpen = (row: ForecastRow) => {
    setEditAnalysisOpen(true);
    setSelectedRow(row);
    console.log('Opened analysis dialog for row:', row);
  };

  const handleAnalysisClose = () => {
    setEditAnalysisOpen(false);
  };

  const segmentOptions = useMemo(() => {
    return useSegments.map(res => {
      return {
        value: res,
        label: `${res.category_name} (${res.number_of_children} categories)`,
      };
    });
  }, [useSegments]);

  const renderDataCell = params => {
    switch (field) {
      case 'revenue':
        if (viewYoY) {
          const value = params.value?.displayYoYRevenue;
          return `${value ? (value * 100).toFixed(2) : '0.00'} %`;
        } else {
          const val = params.value?.displayRevenue;
          return `$ ${val ? val.toFixed(2) : '0.00'}`;
        }
      case 'units':
        if (viewYoY) {
          const value = params.value?.displayYoYUnits;
          return `${value ? (value * 100).toFixed(2) : '0.00'} %`;
        } else {
          const val = params.value?.displayUnits;
          return `$ ${val ? val.toFixed(2) : '0.00'}`;
        }

      default:
        return params.value?.displayRevenue;
    }
  };

  // Growth adjustment columns structure

  const adjustmentColumns = useMemo(
    (): GridColDef<AdjustmentRow>[] => [
      { id: 'categoryName', label: 'Category', flex: 1 },
      {
        id: '1', // each column represents a different year offset from the base year.
        label: useYearSelected.year + 1,
        flex: 1,
        renderCell: params => (
          <NumericInputCell
            value={params.value}
            row={params.row}
            yearOffset={1}
            onValueChange={handleCellValueChange}
          />
        ),
      },
      {
        id: '2',
        label: (useYearSelected.year + 2) as string,
        flex: 1,
        renderCell: params => (
          <NumericInputCell
            value={params.value}
            row={params.row}
            yearOffset={2}
            onValueChange={handleCellValueChange}
          />
        ),
      },
      {
        id: '3',
        label: (useYearSelected.year + 3) as string,
        flex: 1,
        renderCell: params => (
          <NumericInputCell
            value={params.value}
            row={params.row}
            yearOffset={3}
            onValueChange={handleCellValueChange}
          />
        ),
      },
      {
        id: '4',
        label: (useYearSelected.year + 4) as string,
        flex: 1,
        renderCell: params => (
          <NumericInputCell
            value={params.value}
            row={params.row}
            yearOffset={4}
            onValueChange={handleCellValueChange}
          />
        ),
      },
      {
        id: '5',
        label: (useYearSelected.year + 5) as string,
        flex: 1,
        renderCell: params => (
          <NumericInputCell
            value={params.value}
            row={params.row}
            yearOffset={5}
            onValueChange={handleCellValueChange}
          />
        ),
      },
    ],
    [useYearSelected.year]
  );

  const dataColumns = useMemo(
    (): GridColDef<Row>[] => [
      { id: 'categoryName', label: 'Category', flex: 1.5, sortable: true },
      {
        id: '-1',
        label: (useYearSelected.year - 1) as string,
        flex: 1,
        renderCell: params => {
          return renderDataCell(params);
        },
      },
      {
        id: '0',
        label: useYearSelected.year as string,
        flex: 1,
        renderCell: params => {
          return renderDataCell(params);
        },
      },
      {
        id: '1',
        label: (useYearSelected.year + 1) as string,
        flex: 1,
        renderCell: params => {
          return renderDataCell(params);
        },
      },
      {
        id: '2',
        label: (useYearSelected.year + 2) as string,
        flex: 1,
        renderCell: params => {
          return renderDataCell(params);
        },
      },
      {
        id: '3',
        label: (useYearSelected.year + 3) as string,
        flex: 1,
        renderCell: params => {
          return renderDataCell(params);
        },
      },
      {
        id: '4',
        label: (useYearSelected.year + 4) as string,
        flex: 1,
        renderCell: params => {
          return renderDataCell(params);
        },
      },
      {
        id: '5',
        label: (useYearSelected.year + 5) as string,
        flex: 1,
        renderCell: params => {
          return renderDataCell(params);
        },
      },
      {
        id: 'cagrRevenue',
        label: 'CAGR',
        flex: 1,
        renderCell: params => {
          return `${params.value?.toFixed(2)} %`;
        },
      },
    ],
    [useYearSelected, viewYoY]
  );

  const menuItems = [
    {
      label: 'Edit Forecast Analysis',
      onClick: handleAnalysisOpen,
    },
  ];

  // Handle cell value change for growth adjustment using the new utility

  interface NumericInputCellParams {
    value: number;
    row: AdjustmentRow;
    yearOffset: number;
    onValueChange: (yearOffset: number, categoryId: string, factor: number) => void;
  }
  const NumericInputCell = ({ value, row, yearOffset, onValueChange }: NumericInputCellParams) => {
    const numericValue = typeof value === 'string' ? parseFloat(value.replace('%', '')) : value;
    const [inputValue, setInputValue] = useState(numericValue);
    const [previousValue, setPreviousValue] = useState(numericValue);

    const handleChange = useCallback(e => {
      const newValue = e.target.value;
      setInputValue(newValue);
    }, []);

    const handleBlur = useCallback(() => {
      const parsedValue = parseFloat(inputValue) || 0;

      // Only trigger calculation if value has changed
      if (parsedValue !== previousValue) {
        setPreviousValue(parsedValue);
        if (onValueChange) {
          console.log('Calling onValueChange with:', row.id, yearOffset, parsedValue);
          onValueChange(yearOffset, row.id, parsedValue);
        }
      }
    }, [inputValue, previousValue, onValueChange, row.id, yearOffset]);

    return (
      <TextField
        variant="outlined"
        size="small"
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        type="number"
        inputProps={{
          style: { textAlign: 'right' },
          step: '0.1',
          min: '-100',
        }}
        fullWidth
      />
    );
  };

  const handleSwitchChange = event => {
    setViewYoY(event.target.checked);
    console.log('viewYoY changed to:', event.target.checked);
  };

  // Create payload for updating segment forecasts
  const createForecastPayload = useCallback(() => {
    // First, get the user ID (assumed to be available in the component)
    const userId = useStudyProfile?.id || localStorage.getItem('userId'); // Adjust as needed

    const segmentId = selectedCategory?.id;
    console.log('Creating forecast payload for segment:', segmentId);
    // Map the growth data to match the forecast API structure
    const forecastPayload = [];
    console.log(forecastState.forecastRows);
    Object.values(forecastState.forecastRows).forEach(row => {
      console.log('Processing row for forecast payload:', row);
      // Create an array of forecast items for each year
      for (let yearOffset = 1; yearOffset <= 5; yearOffset++) {
        const forecast = row[yearOffset];
        forecastPayload.push({
          id: forecast.id,
          study_year_category_id: row.id,
          year_offset: forecast.yearOffset,
          revenue: forecast.displayRevenue,
          //units_sold: forecast.units_sold,
        });
      }
    });

    return forecastPayload;
  }, [useStudyProfile?.id, selectedCategory?.id, forecastState.forecastRows]);

  // Method to submit the forecast data
  const submitForecastData = useCallback(async () => {
    try {
      console.log('Submitting forecast data for category:', selectedCategory.id);
      const payload = createForecastPayload();
      console.log('Submitting forecast data:', payload);
      // Dispatch the action to update the forecast
      await dispatch(
        studyYearCategories.updateCategoryForecasts({
          studyYearCategoryId: selectedCategory.id,
          forecasts: payload,
        })
      );

      // Show success message
      // setSuccessMessage('Forecast data updated successfully');
    } catch (error) {
      setErrorMessage('Failed to update forecast data');
    }
  }, [selectedCategory, createForecastPayload, dispatch]);

  const submitForecastAnalysis = useCallback(
    async (data: AnalysisSubmitData) => {
      try {
        console.log('Submitting forecast analysis:', data.analysis);
        //const userId = localStorage.getItem('userId');
        const userId = 'B831FFAA-3E69-4A93-86ED-10E0FF6088AC'; // ADDRESS THIS
        await dispatch(
          studyYearCategories.updateStudyYearCategory({
            id: data.id,
            user_id: userId,
            studyYearCategoryId: data.id,
            forecast_analysis: data.analysis,
          })
        );
        handleAnalysisClose();
      } catch (error) {
        console.log('Error submitting forecast analysis:', error);
        setErrorMessage('Failed to submit forecast analysis');
      }
    },
    [dispatch]
  );

  const parentMap = new Map<string, TreeViewNode[]>();
  const makeSegmentTreeData = (): TreeViewNode[] => {
    const convertToTreeNode = (item: any, parentId: string | null = null): TreeViewNode => {
      const segmentName = item.category_name || item.name;
      const childrenNodes = item.children
        ? item.children.map(child => convertToTreeNode(child))
        : [];
      if (parentId) {
        parentMap.set(item.id, childrenNodes);
      }
      return {
        id: item.study_year_category_id || item.id,
        label:segmentName,
        nodeData: item,
        children: childrenNodes,
      };
    };

    return useSegments.map(res => convertToTreeNode(res));
  };
  const segmentTreeData = useMemo(() => makeSegmentTreeData(), [useSegments]);

  const findRootNodeId = (nodeId: string): string | null => {
    let currentNodeId = nodeId;
    while (parentMap.has(currentNodeId)) {
      currentNodeId = parentMap.get(currentNodeId);
    }
    // Return null if no root found
  };
  const isSelectedCategoryAnchor = (): boolean => {
    if (!selectedCategory || !useCurrentYear?.anchor_category) return false;

    return (
      selectedCategory.study_year_category_id ===
      useCurrentYear.anchor_category.study_year_category_id
    );
  };

  // #endregion

  // #region ================== API CALLS ================
  const fetchSegments = async () => {
    // setLoading(true);

    try {
      await dispatch(studyYears.getStudyYearSegments(useYearSelected?.id));
    } catch (error) {
      console.error('Error fetching segments:', error);
    }

    // setLoading(false);
  };

  const fetchChildData = async () => {
    console.log('Fetching child data for category:', selectedCategory.id);
    await dispatch(
      studyYearCategories.getChildCategoriesData({ studyYearCategoryId: selectedCategory.id })
    );

    //setSelectedSegment(arg);
    // setLoading(false);
  };
  const fetchTotalForecasts = async () => {
    console.log('Fetching total forecasts for category:', selectedCategory.id);
    await dispatch(
      studyYearCategories.getStudyYearCategoryForecasts({
        studyYearCategoryId: selectedCategory.id,
      })
    );
  };
  // #endregion

  // #region ================== EFFECTS ================

  useEffect(() => {
    fetchSegments();
   // console.log('Fetching segments for study:', useStudyProfile?.name);
  }, [dispatch, useStudyProfile]);

  useEffect(() => {
    <ForecastAnalysisDialog
      row={selectedRow}
      open={editAnalysisOpen}
      onClose={handleAnalysisClose}
      submitData={submitForecastAnalysis}
    />;
    if (useCurrentYear?.anchor_category && !selectedCategory) {
      setSelectedSegment(useCurrentYear.anchor_category.category_name);
      setSelectedCategory(useCurrentYear.anchor_category);
      //console.log('Selected anchor category:', useCurrentYear.anchor_category.category_name);
      return;
    }
  }, [useSegments, useStudyProfile]);

  useEffect(() => {
    if (selectedCategory) {
      fetchChildData();
      fetchTotalForecasts();
    }
  }, [selectedCategory, useStudyProfile]);

  // Process and transform API data. Initialize ForecastState
  useEffect(() => {
    if (
      selectedCategory &&
      useChildCategoryData &&
      useChildCategoryData.length > 0 &&
      useCategoryForecasts.length > 0
    ) {
      //console.log('Processing child category data and forecasts');
      //console.log('selectedCategory:', selectedCategory);
      const categories: Category[] = [];
      const forecastRows: Record<string, ForecastRow> = {};
      const adjustmentRows: Record<string, AdjustmentRow> = {};
      const totalRow: TotalRow = {
        id: 'total',
        categoryName: 'Total',
        cagrRevenue: 0,
        cagrUnits: 0,
      };
      const columnAgg: Record<number, ColumnAgg> = {};
      //console.log("category data", useChildCategoryData[0]);
      useChildCategoryData.forEach(category => {
        //console.log("processing category", category.category_name, category.yoy_revenue_percentage, category.yoy_units_percentage);
        categories.push({ ...category });
        const newAdjustmentRow: AdjustmentRow = {
          id: category.id,
          categoryId: category.id,
          categoryName: category.category_name,
          0: 0,
          1: 0,
          2: 0,
          3: 0,
          4: 0,
          5: 0,
        };
        const newRow: ForecastRow = {
          id: category.id,
          categoryId: category.id,
          categoryName: category.category_name,
          cagrRevenue: 0,
          cagrUnits: 0,
          analysis: category.forecast_analysis,
        };
        //console.log('Forecast Analysis for category', category.category_name, ':', newRow.analysis);
        if (category.history.length > 0) {
          // We load all the history into data rows even though we only display the most recent year in the table. the other rows will show in the chart.
          category.history.forEach(record => {
            const yearOffset = record.display_year - useYearSelected.year;
            //console.log("Adding history record for category", category.id, "yearOffset:", yearOffset, "record:", record);
            newRow[yearOffset] = {
              id: category.id,
              yearOffset: yearOffset,
              initialRevenue: record.revenue,
              initialUnits: record.units_sold,
              displayRevenue: record.revenue,
              displayUnits: record.units_sold,
              initialYoYRevenue: record.yoy_revenue_percentage,
              initialYoYUnits: record.yoy_units_percentage,
              displayYoYRevenue: record.yoy_revenue_percentage,
              displayYoYUnits: record.yoy_units_percentage,
            };
          });
        }
        const year0 = {
          id: category.id,
          yearOffset: 0,
          initialRevenue: category.revenue,
          initialUnits: category.units_sold,
          displayRevenue: category.revenue,
          displayUnits: category.units_sold,
          initialYoYRevenue: category.yoy_revenue_percentage,
          initialYoYUnits: category.yoy_units_percentage,
          displayYoYRevenue: category.yoy_revenue_percentage,
          displayYoYUnits: category.yoy_units_percentage,
        };
        //console.log("Initialized year 0 for category", category.id, year0.displayYoYRevenue);

        newRow[0] = year0;

        //console.log("new row after adding year 0", newRow)
        //console.log("year 0 for category", category.id, newRow[0]);
        //console.log("newRow[0].displayYoYRevenue:", newRow[0].displayYoYRevenue);
        //console.log("same object? ", newRow[0] === year0);
        category.forecasts.forEach(forecast => {
          //console.log("adding forecast", forecast.year_offset)
          const newForecast: DataItem = {
            id: forecast.id,
            yearOffset: forecast.year_offset,
            initialRevenue: forecast.revenue,
            initialUnits: forecast.units_sold,
            displayRevenue: forecast.revenue,
            displayUnits: forecast.units_sold,
            initialYoYRevenue: forecast.yoy_revenue_percentage,
            initialYoYUnits: forecast.yoy_units_percentage,
            displayYoYRevenue: forecast.yoy_revenue_percentage,
            displayYoYUnits: forecast.yoy_units_percentage,
          };
          newRow[forecast.year_offset] = newForecast;
        });
        newRow.cagrRevenue = ForecastUtils.calculateCAGR(
          newRow[0]?.displayRevenue,
          newRow[5]?.displayRevenue,
          5
        );
        newRow.cagrUnits = ForecastUtils.calculateCAGR(
          newRow[0]?.displayUnits,
          newRow[5]?.displayUnits,
          5
        );
        //console.log("newRow after adding forecasts", newRow);
        forecastRows[category.id] = newRow;
        adjustmentRows[category.id] = newAdjustmentRow;
      });
      const year0Total: DataItem = {
        id: selectedCategory.id,
        categoryId: 'totalId',
        yearOffset: 0,
        initialRevenue: selectedCategory.revenue,
        initialUnits: selectedCategory.units_sold,
        displayRevenue: selectedCategory.revenue,
        displayUnits: selectedCategory.units_sold,
        initialYoYRevenue: selectedCategory.yoy_revenue_percentage,
        initialYoYUnits: selectedCategory.yoy_units_percentage,
        displayYoYRevenue: selectedCategory.yoy_revenue_percentage,
        displayYoYUnits: selectedCategory.yoy_units_percentage,
      };
      totalRow[0] = year0Total;

      useCategoryForecasts.forEach(forecast => {
        const total: DataItem = {
          id: forecast.id,
          categoryId: 'totalId',
          yearOffset: forecast.year_offset,
          displayRevenue: forecast.revenue,
          displayUnits: forecast.units_sold,
          displayYoYRevenue: forecast.yoy_revenue_percentage,
          displayYoYUnits: forecast.yoy_units_percentage,
        };
        totalRow[forecast.year_offset] = total;
      });
      totalRow.cagrRevenue = ForecastUtils.calculateCAGR(
        totalRow[0]?.displayRevenue,
        totalRow[5]?.displayRevenue,
        5
      );
      totalRow.cagrUnits = ForecastUtils.calculateCAGR(
        totalRow[0]?.displayUnits,
        totalRow[5]?.displayUnits,
        5
      );
      const isAnchor = isSelectedCategoryAnchor();
      console.log('isAnchor:', isAnchor);
      forecastDispatch({
        type: 'INITIALIZE_FORECAST',
        payload: {
          forecastRows,
          adjustmentRows,
          totalRow,
          columnAgg,
          isAnchor,
        },
      });
    }
  }, [useChildCategoryData, useCategoryForecasts, selectedCategory]);

  // #endregion

  return (
    <Box className={styles.container}>
      <h1 className={styles.header}>{useStudyName}</h1>
      <div className={styles.mainView}>
        <div className={styles.treeViewContainer}>
          {' '}
          {/* You may need to add this CSS class */}
          <AtomTreeView
            nodes={segmentTreeData}
            onNodeSelect={(event, nodeId) => {
              if (!nodeId) return;

              // Function to find node recursively in tree structure
              const findNodeById = (
                nodes: TreeViewNode[],
                targetId: string
              ): TreeViewNode | null => {
                for (const node of nodes) {
                  if (node.id === targetId) {
                    return node;
                  }
                  if (node.children && node.children.length > 0) {
                    const found = findNodeById(node.children, targetId);
                    if (found) return found;
                  }
                }
                return null;
              };

              const selectedNode = findNodeById(segmentTreeData, nodeId);
              if (selectedNode) {
                const segmentName =
                  selectedNode.nodeData.category_name || selectedNode.nodeData.name;
                setSelectedSegment(segmentName);
                //console.log('Selected segment:', segmentName);
                setSelectedCategory(selectedNode.nodeData);
                //console.log('Selected category:', selectedNode.nodeData);
              }
              const rootNodeId = findRootNodeId(nodeId);
              if (rootNodeId) {
                const rootNode = findNodeById(segmentTreeData, rootNodeId);
              }
            }}
            maxHeight="600px"
            variant="default"
            showIcons={false}
            defaultExpandedItems={[]}
            className={styles.treeView} // You may need to add this CSS class
          />
        </div>
        <div style={{ width: '100%', marginLeft: '16px' }}>
          <RevenueForecastChart
            revenueData={Object.values(forecastState.forecastRows)}
            //  historyData={segmentMarketData?.categories}
            showYOY={autoUpdate}
            onYOYToggle={setAutoUpdate}
            loading={isLoading}
            title={`${useStudyName} - Revenue Forecast by Category`}
            height={350}
          />

          {isLoading ? (
            <ForecastLoadingState
              categoryCount={5} // Adjust based on expected number of categories
              containerStyles={styles.modelCard}
              showBalanceWarning={false} // Set to true if you want to show warning skeleton
            />
          ) : (
            <>
              {error && <Alert severity="error">Error loading market data: {error}</Alert>}

              <div className={styles.modelCard}>
                <h1 className={styles.header}>Forecast</h1>
                <h6 className={styles.subHeader}>
                  {' '}
                  {isSelectedCategoryAnchor() ? 'You are editing an anchor category' : ''}
                </h6>

                {balanceWarning && (
                  <Alert severity="warning" className={styles.balanceWarning}>
                    Warning: The adjusted growth values result in a total that differs significantly
                    from the original. Please review your adjustments.
                  </Alert>
                )}

                <div className={styles.growthSection}>
                  <div className={styles.options}>
                    <h3>Growth Adjustment</h3>
                  </div>

                  <OrgDataTable
                    columns={adjustmentColumns}
                    data={Object.values(forecastState.adjustmentRows)}
                    // totalRows={data.length}
                    totalRows={5}
                    disableColumnMenu={true}
                    disableColumnResize={false}
                    enableZebraStripes={true}
                    hideFooter={true}
                  />
                </div>

                <div className={styles.revenueSection}>
                  <div className={styles.instructionRow}>
                    <h4 className={styles.instruction}>
                      Please adjust above growth for updated revenue
                    </h4>
                    <FormControlLabel
                      control={
                        <Switch checked={viewYoY} onChange={handleSwitchChange} color="primary" />
                      }
                      label="YOY Percentage"
                      labelPlacement="start"
                    />
                  </div>
                  <div className={styles.revenueOptions}>
                    <h3>Revenue</h3>
                  </div>
                  <OrgDataTable
                    columns={dataColumns}
                    data={[...Object.values(forecastState.forecastRows), forecastState.totalRow]}
                    totalRows={selectedCategory?.children?.length || 0}
                    disableColumnMenu={true}
                    disableColumnResize={false}
                    enableZebraStripes={true}
                    hideFooter={true}
                    renderActions={(row, index) => (
                      <OrgActionMenu
                        menuItems={menuItems}
                        rowData={row}
                        index={index}
                        onActionClick={action => {
                          console.log('Action clicked:', action);
                          console.log('Row data:', row);
                          if (action.label === 'Edit Forecast Analysis') {
                            console.log('Opening analysis dialog for row:', row);
                            handleAnalysisOpen(row);
                          }
                        }}
                      />
                    )}
                  />
                </div>

                <AtomButton
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={submitForecastData}
                >
                  Submit
                </AtomButton>
              </div>
            </>
          )}
        </div>
      </div>
      {selectedRow && (
        <ForecastAnalysisDialog
          row={selectedRow}
          open={editAnalysisOpen}
          onClose={handleAnalysisClose}
          submitData={submitForecastAnalysis}
        />
      )}
    </Box>
  );
};

export default Forecast;
